import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layers, Tag, Building2, Network, ArrowRight, Compass } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import SearchBar from "@/components/SearchBar";
import { getStats, SITE_URL } from "@/lib/api";

function StatCard({ n, label, to, Icon, color }) {
  return (
    <Link
      to={to}
      className="card-hover flex items-center gap-3 rounded-[6px] border border-[var(--line)] bg-white px-4 py-4 no-underline"
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-[6px] text-white" style={{ background: color }}>
        <Icon size={20} />
      </span>
      <span>
        <span className="block text-2xl font-bold text-[var(--navy)]">{n}</span>
        <span className="text-sm font-semibold text-[var(--muted)]">{label}</span>
      </span>
    </Link>
  );
}

export default function Home() {
  const { data } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "CompuBrain Knowledge Base",
      url: SITE_URL + "/",
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "CompuBrain",
      url: "https://compubrain.com",
      description:
        "CompuBrain is the parent company providing digital services, technology solutions and platform expertise described across this knowledge base.",
    },
  ];

  const mains = data?.featured_categories || [];
  const services = data?.featured_services || [];
  const companies = data?.featured_companies || [];

  return (
    <Layout>
      <Seo
        title="CompuBrain Knowledge Base — Digital Services, Categories & Technology Platforms"
        description="An interconnected knowledge base of digital services, categories and technology platforms. Explore how services, categories and platforms relate — powered by CompuBrain."
        keywords="digital services, technology platforms, IT services, web development, AI services, SEO, cloud hosting, software development, CompuBrain, knowledge base"
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--line)] bg-[var(--navy)]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 60%, #fff 1px, transparent 1px)",
            backgroundSize: "42px 42px, 60px 60px",
          }}
        />
        <div className="relative mx-auto max-w-[1000px] px-4 py-16 text-center lg:py-20">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[var(--gold)]">
            <Network size={13} /> Interconnected Knowledge Base
          </div>
          <h1 className="mx-auto max-w-3xl text-4xl font-bold leading-tight text-white sm:text-5xl">
            Explore digital services, categories &amp; technology platforms
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-white/75">
            Start anywhere and follow the links — every service, category and platform is
            connected, Wikipedia-style, so you can discover how it all fits together.
          </p>
          <div className="mx-auto mt-7 max-w-2xl">
            <SearchBar variant="hero" />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-white/70">
            <span>Popular:</span>
            {["ai-chatbot-development", "seo", "website-development", "cloud-and-hosting"].map((s, i) => (
              <Link
                key={s}
                to={`/${i === 1 || i === 3 ? "category" : "service"}/${s}`}
                className="rounded-full border border-white/20 px-3 py-1 text-white/85 no-underline hover:border-[var(--gold)] hover:text-white"
              >
                {s.replace(/-/g, " ")}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="mx-auto max-w-[1240px] px-4 py-8 lg:px-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard n={data?.services ?? "—"} label="Services" to="/services" Icon={Layers} color="#16385f" />
          <StatCard n={data?.categories ?? "—"} label="Categories" to="/categories" Icon={Tag} color="#b1560f" />
          <StatCard n={data?.companies ?? "—"} label="Platforms" to="/companies" Icon={Building2} color="#0b6b3a" />
        </div>
      </section>

      {/* MAIN CATEGORIES */}
      <section className="mx-auto max-w-[1240px] px-4 pb-4 lg:px-6" data-testid="home-main-categories">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[var(--navy)]">Browse main categories</h2>
            <p className="text-sm text-[var(--muted)]">Top-level areas of the knowledge base.</p>
          </div>
          <Link to="/categories" className="hidden items-center gap-1 text-sm font-semibold text-[var(--gov-blue)] hover:underline sm:flex">
            All categories <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {mains.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`home-cat-${c.slug}`}
              className="card-hover group rounded-[6px] border border-[var(--line)] bg-white p-4 no-underline"
            >
              <Tag size={16} className="mb-2 text-[var(--gov-blue)]" />
              <span className="block text-sm font-bold text-[var(--ink)] group-hover:text-[var(--gov-blue)]">
                {c.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT CONNECTS */}
      <section className="mx-auto mt-6 max-w-[1240px] px-4 lg:px-6">
        <div className="rounded-[6px] border border-[var(--line)] bg-[var(--bg-panel)] p-6">
          <div className="flex items-center gap-2 text-[var(--navy)]">
            <Compass size={18} />
            <h2 className="text-xl font-bold">How the knowledge base connects</h2>
          </div>
          <p className="mt-2 max-w-3xl text-[15px] text-[var(--ink-soft)]">
            Every entry links to related services, categories and platforms. For example, from{" "}
            <Link to="/service/a-b-testing" className="wikilink font-semibold">A/B Testing</Link> you can move to{" "}
            <Link to="/category/website-development" className="wikilink font-semibold">Website Development</Link>,{" "}
            <Link to="/category/software-testing" className="wikilink font-semibold">Software Testing</Link> and beyond —
            or from{" "}
            <Link to="/company/openai" className="wikilink font-semibold">OpenAI</Link> to{" "}
            <Link to="/category/ai-services" className="wikilink font-semibold">AI Services</Link> and{" "}
            <Link to="/service/ai-chatbot-development" className="wikilink font-semibold">AI Chatbot Development</Link>.
          </p>
        </div>
      </section>

      {/* FEATURED SERVICES + PLATFORMS */}
      <section className="mx-auto max-w-[1240px] px-4 py-10 lg:px-6">
        <div className="grid gap-8 lg:grid-cols-2">
          <div data-testid="home-featured-services">
            <div className="mb-3 flex items-end justify-between">
              <h2 className="text-xl font-bold text-[var(--navy)]">Featured services</h2>
              <Link to="/services" className="text-sm font-semibold text-[var(--gov-blue)] hover:underline">View all</Link>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {services.slice(0, 12).map((s) => (
                <Link key={s.slug} to={`/service/${s.slug}`} className="card-hover rounded-[5px] border border-[var(--line)] bg-white px-3 py-2 text-sm font-semibold text-[var(--ink)] no-underline hover:text-[var(--gov-blue)]">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
          <div data-testid="home-featured-platforms">
            <div className="mb-3 flex items-end justify-between">
              <h2 className="text-xl font-bold text-[var(--navy)]">Featured platforms</h2>
              <Link to="/companies" className="text-sm font-semibold text-[var(--gov-blue)] hover:underline">View all</Link>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {companies.slice(0, 12).map((s) => (
                <Link key={s.slug} to={`/company/${s.slug}`} className="card-hover rounded-[5px] border border-[var(--line)] bg-white px-3 py-2 text-sm font-semibold text-[var(--ink)] no-underline hover:text-[var(--gov-blue)]">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
